import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled';
import { ButtonGroup } from '@atlaskit/button';
import Button from '@atlaskit/button/new';

import { QUESTION_CONTENT_TYPE } from './contentType.ts';

const i18n = defineMessages({
	recentFilterButton: {
		id: 'questions.questions-list.recent-filter-button',
		defaultMessage: 'Recent',
		description: 'Text for the filter button that filters questions by most recent',
	},
	popularFilterButton: {
		id: 'questions.questions-list.popular-filter-button',
		defaultMessage: 'Popular',
		description: 'Text for the filter button that filters questions by most popular',
	},
	unansweredFilterButton: {
		id: 'questions.questions-list.unasnwered-filter-button',
		defaultMessage: 'Unanswered',
		description: 'Text for the filter button that filters questions by unanswered',
	},
});

export enum FilterType {
	RECENT = 'recent',
	POPULAR = 'popular',
	UNANSWERED = 'unanswered',
}

export type QuestionsFiltersProps = {
	onFilterChange: (cql: string, filterType: FilterType) => void;
	labelFilter?: string;
	disableFilter?: boolean;
	spaceKey?: string;
	currentFilter: FilterType;
};

export const getCQLForFilter = (
	filter: FilterType,
	topics?: string[],
	// TODO: Turn all and any into ENUM's?
	includeTopicsMode?: 'all' | 'any',
	spaceKey?: string,
): string => {
	const baseType = `type="${QUESTION_CONTENT_TYPE}"`;
	const spaceQuery = spaceKey ? ` AND space="${spaceKey}"` : '';

	let labelQuery = '';
	if (topics && topics.length > 0) {
		if (includeTopicsMode === 'any') {
			const labelConditions = topics.map((topic) => `label="${topic}"`).join(' OR ');
			labelQuery = ` AND (${labelConditions})`;
		} else {
			labelQuery = topics.map((topic) => ` AND label="${topic}"`).join('');
		}
	}

	switch (filter) {
		case FilterType.POPULAR:
			return `${baseType}${spaceQuery}${labelQuery} AND atl_cq_votes_total > 0 order by atl_cq_votes_total desc, lastmodified desc`;
		case FilterType.UNANSWERED:
			return `${baseType}${spaceQuery}${labelQuery} AND content.property[atl_cq_has_answers].any_answer=false order by created desc, title`;
		case FilterType.RECENT:
		default:
			return `${baseType}${spaceQuery}${labelQuery} order by created desc, title`;
	}
};

export const QuestionsFilters = ({
	onFilterChange,
	labelFilter,
	disableFilter = false,
	spaceKey,
	currentFilter,
}: QuestionsFiltersProps) => {
	const { formatMessage } = useIntl();

	useEffect(() => {
		const topics = labelFilter ? [labelFilter] : undefined;
		onFilterChange(getCQLForFilter(currentFilter, topics, 'all', spaceKey), currentFilter);
		// Run on mount to initialize the query
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClick = (filter: FilterType) => {
		const topics = labelFilter ? [labelFilter] : undefined;
		onFilterChange(getCQLForFilter(filter, topics, 'all', spaceKey), filter);
	};

	return (
		<Box>
			<ButtonGroup>
				<Button
					onClick={() => handleClick(FilterType.RECENT)}
					isSelected={currentFilter === FilterType.RECENT}
				>
					{formatMessage(i18n.recentFilterButton)}
				</Button>
				<Button
					isDisabled={disableFilter}
					onClick={() => handleClick(FilterType.POPULAR)}
					isSelected={currentFilter === FilterType.POPULAR}
				>
					{formatMessage(i18n.popularFilterButton)}
				</Button>
				<Button
					isDisabled={disableFilter}
					onClick={() => handleClick(FilterType.UNANSWERED)}
					isSelected={currentFilter === FilterType.UNANSWERED}
				>
					{formatMessage(i18n.unansweredFilterButton)}
				</Button>
			</ButtonGroup>
		</Box>
	);
};
